import { Component, Input, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { TemplatesService } from 'app/apps/templates.service';
import { ChartTypes } from 'app/shared/model/charts/chart-types.enum';
import { ErrorHandler } from 'app/shared/service/error-handler.service';

@Component({
  selector: 'cc-charts-creation-wizard',
  templateUrl: './charts-creation-wizard.component.html',
  styleUrls: ['./charts-creation-wizard.component.scss'],
})
export class ChartsCreationWizardComponent implements OnDestroy {
  private readonly spinnerDelay = 1000;
  private unSubscribe = new Subject<void>();
  readonly chartTypes = ChartTypes;

  @Input() teamId;
  @Input() companyId;
  @Input() dashboardId;

  creatingChart: boolean;
  selectedKind: ChartTypes;
  showSpinner: boolean;
  quantityChartTemplatesExist = true;

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly analyticsService: AnalyticsService,
    private readonly errorHandler: ErrorHandler,
    private readonly templatesService: TemplatesService,
  ) {}

  async createChart(kind) {
    if (this.creatingChart) {
      return;
    }

    this.selectedKind = kind;
    this.creatingChart = true;

    // Show spinner after delay
    setTimeout(() => {
      this.showSpinner = true;
    }, this.spinnerDelay);

    try {
      if (!(await this.canCreateChart(kind))) {
        this.quantityChartTemplatesExist = false;
        this.creatingChart = false;
        return;
      }

      let chart;
      if (this.teamId) {
        chart = await this.analyticsService
          .addTeamChart(this.teamId, this.dashboardId, { kind })
          .pipe(takeUntil(this.unSubscribe))
          .toPromise();
      }

      if (this.companyId) {
        chart = await this.analyticsService
          .addCompanyChart(this.companyId, this.dashboardId, { kind })
          .pipe(takeUntil(this.unSubscribe))
          .toPromise();
      }

      this.activeModal.close(chart);
    } catch (error) {
      if (error) {
        this.errorHandler.handle(error);
      }
    } finally {
      this.creatingChart = false;
    }
  }

  private async canCreateChart(kind: ChartTypes) {
    let templates = [];
    if (this.teamId) {
      templates = await this.templatesService
        .getTeamAppsForMakingChart(this.teamId, kind)
        .pipe(takeUntil(this.unSubscribe))
        .toPromise();
    }

    if (this.companyId) {
      templates = await this.templatesService
        .getCompanyAppsForMakingChart(this.companyId, kind)
        .pipe(takeUntil(this.unSubscribe))
        .toPromise();
    }

    return !!templates.length;
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
